import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS, createLazyQuery } from '@/shared/api';
import { fetchKnowledgeSource, fetchKnowledgeSources } from './api';
import type { KnowledgeSourceListParams } from './types';

/**
 * Query keys for knowledge sources
 */
export const knowledgeSourcesKeys = {
  all: [API_TAGS.KNOWLEDGE_SOURCES] as const,
  lists: () => [...knowledgeSourcesKeys.all, 'list'] as const,
  list: (params?: KnowledgeSourceListParams) => [...knowledgeSourcesKeys.lists(), { ...params }] as const,
  details: () => [...knowledgeSourcesKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...knowledgeSourcesKeys.details(), Number(id)] as const,
};

/**
 * Hook to fetch knowledge sources with infinite scroll
 */
export const useInfiniteKnowledgeSources = (params?: KnowledgeSourceListParams) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: knowledgeSourcesKeys.list(params),
    queryFn: ({ pageParam }) => fetchKnowledgeSources({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    retry: false,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalPages = Math.ceil((lastPage.total || 0) / limit);
      const nextPage = lastPageParam + 1;
      return nextPage < totalPages ? nextPage : undefined;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      items: query.data?.pages.flatMap((p) => p.items),
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Хук получения источника знаний по идентификатору.
 * Без идентификатора запрос не уходит, а ключ остаётся на уровне `details()`: ключ детали строится
 * только по реальному идентификатору, иначе в него попал бы `Number(undefined)`
 */
export const useKnowledgeSource = (id?: number | string) => {
  return useQuery({
    queryKey: id ? knowledgeSourcesKeys.detail(id) : knowledgeSourcesKeys.details(),
    queryFn: () => fetchKnowledgeSource(id!),
    enabled: !!id,
    retry: false,
  });
};

/**
 * Хук для ленивой загрузки источников знаний (ручной триггер).
 * Использует кэш React Query — повторный вызов с теми же params не делает HTTP-запрос.
 */
export const useLazyKnowledgeSources = createLazyQuery(
  (params: KnowledgeSourceListParams) => knowledgeSourcesKeys.list(params),
  fetchKnowledgeSources,
  { retry: false },
);
